import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage, SUPPORTED_LANGS } from '@/context/LanguageContext';

const Navbar = () => {
  const { t, language, setLanguage, availableLanguages } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => { setIsScrolled(window.scrollY > 20); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Build lang-prefixed path
  const langPath = (path) => `/${language}${path}`;

  const switchLanguage = (code) => {
    setLanguage(code);
    setIsLangMenuOpen(false);
    // Navigate to the new lang URL, preserving the sub-path
    const pathParts = location.pathname.split('/');
    if (SUPPORTED_LANGS.includes(pathParts[1])) {
      pathParts[1] = code;
      navigate(pathParts.join('/'));
    } else {
      navigate(`/${code}`);
    }
  };

  const navLinks = [
    { key: 'nav_features', href: `/${language}#features` },
    { key: 'nav_pricing', href: langPath('/pricing') },
    { key: 'nav_mobile', href: langPath('/mobile') },
    { key: 'nav_about', href: langPath('/about') },
    { key: 'nav_faq', href: langPath('/faq') },
    { key: 'nav_contact', href: langPath('/contact') },
  ];

  const currentLang = availableLanguages.find(l => l.code === language);

  return (
    <nav 
      className={`vf-nav transition-all duration-300 ${
        isScrolled ? 'shadow-sm' : ''
      }`}
      data-testid="navbar"
    >
      <div className="vf-container">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link 
            to={langPath('')} 
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            <img src="/logo.png" alt="VendFactura" className="h-16 sm:h-20 w-auto object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="text-slate-600 hover:text-[#1e3a5f] font-medium transition-colors"
                data-testid={`nav-link-${link.key}`}
              >
                {t(link.key)}
              </a>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-[#1e3a5f] transition-colors"
                data-testid="language-selector"
              >
                <span>{currentLang?.code.toUpperCase()}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              <AnimatePresence>
                {isLangMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-40 bg-white border-2 border-slate-100 shadow-lg py-2 z-50"
                  >
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => switchLanguage(lang.code)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${
                          language === lang.code ? 'font-bold text-[#1e3a5f]' : 'text-slate-600'
                        }`}
                        data-testid={`lang-option-${lang.code}`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/login"
              className="px-6 py-2 text-[#1e3a5f] font-medium hover:bg-slate-50 transition-colors"
              data-testid="nav-login-btn"
            >
              {t('nav_login')}
            </Link>
            
            <Link
              to="/register"
              className="vf-btn-primary px-6 py-2 text-base"
              data-testid="nav-register-btn"
            >
              {t('nav_register')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 z-50 flex-shrink-0"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#1e3a5f]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1e3a5f]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100"
          >
            <div className="vf-container py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  className="block py-2 text-slate-600 hover:text-[#1e3a5f] font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t(link.key)}
                </a>
              ))}
              
              <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
                {/* Language options */}
                <div className="flex gap-2 pb-4">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`px-3 py-1 text-sm border-2 ${
                        language === lang.code 
                          ? 'border-[#1e3a5f] bg-[#1e3a5f] text-white' 
                          : 'border-slate-200 text-slate-600'
                      }`}
                    >
                      {lang.code.toUpperCase()}
                    </button>
                  ))}
                </div>
                
                <Link
                  to="/login"
                  className="w-full py-3 text-center text-[#1e3a5f] font-medium border-2 border-[#1e3a5f]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav_login')}
                </Link>
                <Link
                  to="/register"
                  className="vf-btn-primary w-full text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav_register')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
